import React, { useContext, useMemo } from 'react';
import { noop } from 'underscore';
import {
  VideoEditorPlaybackTimeContextType,
  VideoEditorPlaybackTimeProviderProps,
} from './types';

const DEFAULT_VIDEO_EDITOR_PLAYBACK_TIME_CONTEXT_VALUE = {
  positionSec: 0,
  seekToSec: undefined,
  setPositionSec: noop,
};

const VideoEditorPlaybackTimeContext = React.createContext<
  VideoEditorPlaybackTimeContextType | undefined
>(undefined);

export const VideoEditorPlaybackTimeProvider: React.FC<VideoEditorPlaybackTimeProviderProps> = ({
  children,
  positionSec = 0,
  seekToSec,
  setPositionSec = noop,
}) => (
  <VideoEditorPlaybackTimeContext.Provider
    value={useMemo(() => ({ positionSec, seekToSec, setPositionSec }), [
      positionSec,
      seekToSec,
      setPositionSec,
    ])}
  >
    {children}
  </VideoEditorPlaybackTimeContext.Provider>
);

export type UseVideoEditorPlaybackTimeContextProps = {
  /**
   * If true, will throw an error if the context is not found.   Most of our context
   * hooks operate as if this value were set to true.
   *
   * This is configurable, and set to `false` by default, to mimic legacy behavior
   * where this context wasn't required and if it wasn't found, a default value
   * was returned.  You never knew whether you were safely using the provider or
   * you were working with a default value.
   *
   * This flag will at least let us turn it on conditionally to start fixing the
   * implementaiton when we come across affected components.
   */
  checkProvider?: boolean;
};

export function useVideoEditorPlaybackTimeContext(
  props?: UseVideoEditorPlaybackTimeContextProps,
): VideoEditorPlaybackTimeContextType {
  const context = useContext(VideoEditorPlaybackTimeContext);
  const { checkProvider = false } = props ?? {};

  if (context === undefined) {
    if (checkProvider) {
      throw new Error(
        'useVideoEditorPlaybackTimeContext must be ' +
          'used within VideoEditorPlaybackTimeProvider',
      );
    }
    return DEFAULT_VIDEO_EDITOR_PLAYBACK_TIME_CONTEXT_VALUE;
  }

  return context;
}

export const VideoEditorPlaybackTimeConsumer =
  VideoEditorPlaybackTimeContext.Consumer;

export function setVideoEditorPlaybackTimeContextType(
  Component: React.ComponentClass,
) {
  Component.contextType = VideoEditorPlaybackTimeContext;
}
